import React from "react";
import { cn } from "../../../common/u1/cn";
import { Dirham, DownloadIcon } from "../../../../assets/ui/icons";
import {
  planLabel,
  rowAmount,
  formatAed,
  isNegative,
  invoiceName,
  invoiceFileHint,
  paidOn,
  invoiceHref,
  invoiceTitle,
} from "./billingFormat";

/* One payment. Every cell's content comes from billingFormat, so the wording
 * and the figures are the live screen's — see the notes there.
 */
const CELL = "px-4 py-3.5 text-[13.5px] font-sans align-middle sm:px-5";

export const InvoiceRow = ({ row, onDownload = () => {} }) => {
  const amount = rowAmount(row);
  const href = invoiceHref(row);

  return (
    <tr className="border-t border-[var(--line)]">
      <td className={cn(CELL, "text-[var(--ink)]")}>
        <span className="flex items-center gap-2.5">
          {/* The design's PDF badge. Literally the letters, not an icon — a
              three-letter chip reads at this size where a glyph would not. */}
          <span
            aria-hidden="true"
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
              "bg-[var(--coral-soft)] text-[8.5px] font-bold tracking-[.02em]",
              "text-[var(--coral-deep)] font-sans"
            )}
          >
            PDF
          </span>
          <span className="font-semibold" title={invoiceFileHint(row) || undefined}>
            {invoiceName(row)}
          </span>
        </span>
      </td>

      <td className={cn(CELL, "whitespace-nowrap text-[var(--ink-2)]")}>{paidOn(row)}</td>

      <td className={cn(CELL, "whitespace-nowrap font-semibold text-[var(--ink)]")}>
        <span className="inline-flex items-baseline">
          {isNegative(amount) && <span className="mr-1">−</span>}
          <Dirham />
          {formatAed(amount)}
        </span>
      </td>

      <td className={cn(CELL, "capitalize text-[var(--ink-2)]")}>{planLabel(row)}</td>

      <td className={cn(CELL, "text-right")}>
        {/* Disabled rather than hidden when a row has no document at all: the
            column stays legible, and the reason is in the tooltip. */}
        <button
          type="button"
          onClick={() => onDownload(href)}
          disabled={!href}
          title={href ? invoiceTitle(row) : "No invoice document for this payment"}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-1 py-0.5",
            "text-[13.5px] font-semibold text-[var(--coral)] font-sans",
            "transition-colors hover:text-[var(--coral-deep)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
            "disabled:cursor-not-allowed disabled:text-[var(--muted-2)] disabled:no-underline"
          )}
        >
          <DownloadIcon size={15} />
          Download
        </button>
      </td>
    </tr>
  );
};

const HEAD = cn(
  "px-4 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[.07em]",
  "text-[var(--muted)] font-sans sm:px-5"
);

export const InvoiceTable = ({ rows = [], onDownload, emptyMessage }) => (
  /* The table scrolls inside its own box rather than widening the page — five
     columns of dates and amounts will not fit a phone. */
  <div className="-mx-px overflow-x-auto">
    <table className="w-full min-w-[620px] border-collapse">
      <thead className="bg-[var(--bg)]">
        <tr>
          <th scope="col" className={HEAD}>Invoice</th>
          <th scope="col" className={HEAD}>Paid on</th>
          <th scope="col" className={HEAD}>Amount</th>
          <th scope="col" className={HEAD}>Product</th>
          <th scope="col" className={cn(HEAD, "text-right")}>Action</th>
        </tr>
      </thead>

      <tbody>
        {rows.length === 0 ? (
          <tr className="border-t border-[var(--line)]">
            <td colSpan={5} className="px-5 py-10 text-center text-[13.5px] text-[var(--muted)] font-sans">
              {emptyMessage || "No payments to show yet."}
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <InvoiceRow
              /* Rows have no stable id of their own — subscriptionID repeats
                 across a subscription's invoices — so the index is part of the
                 key. The list is a read-only page, never reordered in place. */
              key={`${row?.subscriptionID || row?.PaymentIntentID || "row"}-${i}`}
              row={row}
              onDownload={onDownload}
            />
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default InvoiceTable;
